// Imperative "Setting <Name>'s Projector account for you" overlay.
//
// It lives directly on document.body (NOT inside the React page tree) so it
// survives the route remount that happens when you switch projectors
// (/profile → /profile/:id are different route matches). That lets the aurora
// loader stay on screen across the navigation and bridge the next page's load,
// so viewers see this branded loader instead of a long grid shimmer.

let el = null;
let hideTimer = null;

const STYLE_ID = 'proj-switch-overlay-style';

const ensureStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
  @keyframes projSwitchSpin { to { transform: rotate(360deg); } }
  @keyframes projSwitchDrift {
    0%   { transform: translate3d(-4%, -2%, 0) scale(1.05); }
    50%  { transform: translate3d(4%, 3%, 0) scale(1.12); }
    100% { transform: translate3d(-4%, -2%, 0) scale(1.05); }
  }
  .projSwitchOverlay {
    position: fixed; inset: 0; z-index: 2147483000;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 26px; background: #05060a;
    /* Opaque from the first frame — a fade-IN would let the page behind flash
       through the half-transparent overlay while the route remounts. Only the
       fade-OUT is animated (see .hiding). */
    opacity: 1;
    font-family: 'Montserrat', sans-serif;
  }
  .projSwitchOverlay.hiding { opacity: 0; transition: opacity .45s ease; }
  .projSwitchAurora {
    position: absolute; inset: -10%; filter: blur(8px); opacity: .6;
    animation: projSwitchDrift 8s ease-in-out infinite;
  }
  .projSwitchSpinner {
    position: relative; width: 46px; height: 46px; border-radius: 50%;
    border: 3px solid rgba(255,255,255,.16); animation: projSwitchSpin 1s linear infinite;
  }
  .projSwitchText {
    position: relative; color: #fff; font-size: 18px; font-weight: 600;
    letter-spacing: .2px; text-align: center; padding: 0 24px;
  }
  `;
  document.head.appendChild(s);
};

export const showProjectorSwitchOverlay = ({ name = '', color = '' } = {}) => {
  try {
    ensureStyles();
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    if (!el) {
      el = document.createElement('div');
      el.className = 'projSwitchOverlay';
      document.body.appendChild(el);
    }
    // Reused overlay: clear any in-flight fade-out so it's fully opaque again.
    el.classList.remove('hiding');
    const aurora = color
      ? `radial-gradient(55% 45% at 28% 22%, ${color}, transparent 60%), radial-gradient(50% 45% at 76% 82%, ${color}, transparent 62%)`
      : 'radial-gradient(55% 45% at 28% 22%, #1e3a5f, transparent 60%), radial-gradient(50% 45% at 76% 82%, #6d5efc, transparent 62%)';
    el.innerHTML = `
      <div class="projSwitchAurora"></div>
      <div class="projSwitchSpinner"></div>
      <div class="projSwitchText"></div>
    `;
    el.querySelector('.projSwitchAurora').style.background = aurora;
    el.querySelector('.projSwitchSpinner').style.borderTopColor = color || '#8ab4ff';
    // textContent (not innerHTML) so a projector display name can't inject markup.
    el.querySelector('.projSwitchText').textContent = `Setting ${name ? `${name}'s` : ''} Projector account for you`;
  } catch (e) { /* noop */ }
};

export const hideProjectorSwitchOverlay = (delay = 0) => {
  const doHide = () => {
    hideTimer = null;
    if (!el) return;
    const node = el;
    el = null;
    node.classList.add('hiding');
    setTimeout(() => { try { node.remove(); } catch (e) { /* noop */ } }, 500);
  };
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
  if (delay > 0) hideTimer = setTimeout(doHide, delay);
  else doHide();
};
